import useSWR from 'swr';
import type { Deal, DealIndexClient, DealSearchCondition } from '~/models/Deal';
import type { SwrReturnType } from '~/models/Api';
import { convertQueryStringUrl } from '~/utils/DealSearchConditionUtils';
import { generatePath } from '~/utils/generatePath';

const paths = {
  index: '/deals',
  show: '/deals/:id'
} as const;
type TIndex = (
  query: DealSearchCondition
) => SwrReturnType<Pageable<DealIndexClient>>;
type TShow = (id: number | null) => SwrReturnType<Deal>;
type ReturnType = {
  index: TIndex;
  show: TShow;
};

const buildShowPath = (id: number): string => generatePath(paths.show, { id });

export const dealCacheKey = (id: number): string => buildShowPath(id);

export const useDeals = (): ReturnType => {
  const index: TIndex = (query: DealSearchCondition) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, mutate, error } = useSWR<Pageable<DealIndexClient>>(
      convertQueryStringUrl(paths.index, query)
    );
    return {
      data: data ?? null,
      mutate,
      isLoading: !data && !error,
      error
    };
  };
  const show: TShow = (id: number | null) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, mutate, error } = useSWR<Deal>(
      id === null ? null : paths.show.replace(':id', String(id))
    );
    return {
      data: data ?? null,
      mutate,
      isLoading: !data && !error,
      error
    };
  };
  return { index, show };
};
